import { useLayoutEffect } from 'react';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react-dom';

import { SAVED_DOCUMENT_PANEL_GAP } from '../lib/const';
import type { SavedDocumentPanelAnchor } from '../store';

const VIEWPORT_PADDING = 8;

interface Params {
  rootElement: HTMLElement | null;
  // How much of the top of the scrolled area is covered, by a sticky toolbar or a fixed bar.
  getCoveredTop: () => number;
  anchor: SavedDocumentPanelAnchor;
}

/**
 * Places a panel below the text or bubble it opens from, or above it when there is no room below.
 * The anchor is an offset within the root element, and the panel is positioned in the same box.
 */
export const usePanelPlacement = ({ rootElement, getCoveredTop, anchor }: Params) => {
  const { refs, y, isPositioned } = useFloating({
    open: true,
    strategy: 'absolute',
    placement: 'bottom',
    middleware: [
      offset(SAVED_DOCUMENT_PANEL_GAP),
      flip(() => ({
        crossAxis: false,
        padding: {
          top: getCoveredTop() + VIEWPORT_PADDING,
          bottom: VIEWPORT_PADDING,
        },
      })),
    ],
    whileElementsMounted: autoUpdate,
  });

  const { top: anchorTop, bottom: anchorBottom } = anchor;
  useLayoutEffect(() => {
    if (!rootElement) {
      return;
    }
    refs.setReference({
      contextElement: rootElement,
      getBoundingClientRect: () => {
        const rootRect = rootElement.getBoundingClientRect();
        const top = rootRect.top + anchorTop;
        const height = anchorBottom - anchorTop;
        return {
          x: rootRect.left,
          y: top,
          top,
          bottom: top + height,
          left: rootRect.left,
          right: rootRect.right,
          width: rootRect.width,
          height,
        };
      },
    });
  }, [refs, rootElement, anchorTop, anchorBottom]);

  // Until it is placed, the panel stays transparent rather than hidden, so its input can take focus.
  return {
    setPanelElement: refs.setFloating,
    panelStyle: { top: y, opacity: isPositioned ? undefined : 0 },
  };
};
